import {Document as PDFDocument, Page, pdfjs} from 'react-pdf';
import React, {useState} from 'react';
import '../drivers/AnnotationLayer.css';
import '../drivers/TextLayer.css';
import {
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon
} from "@heroicons/react/24/outline";
import {ChevronLeftIcon, ChevronRightIcon, DocumentDuplicateIcon, DocumentIcon} from "@heroicons/react/20/solid";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import {classNames} from "../../../../util/util-helpers";
import {LoaderLarge} from "../../../loader";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

export default function PDFViewerNew({filePath, defaultZoom = 1.5}) {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [value, setValue] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isSinglePageView, setIsSinglePageView] = useState(false);
    const minZoom = 0.5;
    const maxZoom = 2.5;
    const [zoom, setZoom] = useState(defaultZoom);

    const zoomIn = () => setZoom(z => Math.min(z + 0.2, maxZoom));
    const zoomOut = () => setZoom(z => Math.max(z - 0.2, minZoom));
    const zoomReset = () => setZoom(defaultZoom);

    function onDocumentLoadSuccess({numPages}) {
        setNumPages(numPages);
        setCurrentPage(1);
        setValue(1);
        setIsLoading(false);
    }

    const handlePageLoad = async (pdfPage) => {
        const rotate = pdfPage.rotate ?? 0

        if (pdfPage.rotate && !rotation) {
            setRotation(rotate);
        }
    };

    return (
        <div>
            <div
                className="sticky left-0 top-0 mb-2 z-20 inline-flex items-center gap-x-4 gap-1 bg-inverse py-1 px-4 w-full border-b border-dashed border-tm-gray-300">

                {isSinglePageView && (
                    <>
                        <button
                            onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                            disabled={pageNumber === 1}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-5 w-5 mr-1"/>
                            Prev
                        </button>

                        <span className="text-gray-700 font-medium">
                    Page {pageNumber} of {numPages}
            </span>

                        <button
                            onClick={() => setPageNumber((p) => Math.min(p + 1, numPages))}
                            disabled={pageNumber === numPages}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRightIcon className="h-5 w-5 ml-1"/>
                        </button>
                    </>
                )}

                <button
                    onClick={zoomOut}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                    disabled={zoom <= minZoom}
                >
                    <MagnifyingGlassMinusIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={zoomReset}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <MagnifyingGlassIcon className={'w-7 h-7 z-10'}/>
                </button>

                <button
                    onClick={zoomIn}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                    disabled={zoom >= maxZoom}
                >
                    <MagnifyingGlassPlusIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={() => setRotation((r) => (r - 90) % 360)}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <ArrowUturnLeftIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <ArrowUturnRightIcon className={'w-7 h-7'}/>
                </button>

                {numPages > 1 && (
                    <button
                        onClick={() => {
                            setRotation(0);
                            setZoom(defaultZoom);
                            setIsSinglePageView(!isSinglePageView)
                        }}
                        className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                        aria-label="Toggle single/all pages view"
                    >
                        {!isSinglePageView ? (
                            <DocumentIcon className="w-7 h-7"/>
                        ) : (
                            <DocumentDuplicateIcon className="w-7 h-7"/>
                        )}
                    </button>
                )}
            </div>
            <div>
                {!!isLoading && (
                    <div key={'pdfLoading'} className={"grid justify-center items-center"}><LoaderLarge disableContainer={true}/></div>)}

                <div className="w-full overflow-auto">
                    <PDFDocument
                        file={filePath}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={null}
                    >
                        {isSinglePageView ? (
                            <div
                                className={classNames("grid justify-center align-middle")}>
                                <Page pageNumber={pageNumber} renderTextLayer scale={zoom} rotate={rotation}
                                      loading={null} onLoadSuccess={handlePageLoad}/>
                            </div>
                        ) : (
                            Array.from(new Array(numPages), (el, index) => (
                                <div
                                    key={`outer_page_container_${index + 1}`}
                                    className={classNames("grid justify-center align-middle", index > 0 ? "mt-4 border-t-8 border-tm-gray-300 border-dashed pt-4 w-full" : undefined)}>
                                    <div
                                        key={`page_container_${index + 1}`}
                                    >
                                        <Page key={`page_${index + 1}`} pageNumber={index + 1} renderTextLayer
                                              scale={zoom}
                                              className={"border-2 border-tm-gray-300 mx-auto"}
                                              rotate={rotation} renderAnnotationLayer={true} loading={null} onLoadSuccess={handlePageLoad}/>
                                    </div>
                                </div>
                            ))
                        )}
                    </PDFDocument>
                </div>

                {isSinglePageView && (
                    <div className={"flex justify-center align-middle"}>
                        <button
                            onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                            disabled={pageNumber === 1}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-5 w-5 mr-1"/>
                            Prev
                        </button>

                        <div className="flex text-gray-700 font-medium items-center mx-4">
                            Page {pageNumber} of {numPages}
                        </div>

                        <button
                            onClick={() => setPageNumber((p) => Math.min(p + 1, numPages))}
                            disabled={pageNumber === numPages}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRightIcon className="h-5 w-5 ml-1"/>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}